import { useField } from 'formik'
import React, { useRef, useState } from 'react'
import FWOTextInput from '../form/FWOTextInput'
import AddressService from '../../services/AnonymousReportService'
import FWOAutoComplete from '../form/FWOAutoComplete'
import { ILocation } from '../../models/Address'
import useErrorHandler from '../../hooks/useErrorHandler'
import Conditional from '../common/Conditional'

export interface IFWOSuburbPostcodeLookupProps {
  postcodeFieldId: string
  suburbFieldId: string
  stateFieldId: string
  postcodeLabel: string
  suburbLabel: string
  stateLabel: string
  placeholder?: string
  titleSuburb?: string
  titlePostcode?: string
  titleState?: string
  showreadonly?: boolean
  showotherreadonly?: boolean
  tooltip?: string
  required: boolean
  displayOrder?: boolean
  requiredPostcode?: boolean
  requiredState?: boolean
}

const FWOSuburbPostcodeLookup = (props: IFWOSuburbPostcodeLookupProps) => {
  const [, , postCodeHelpers] = useField(props.postcodeFieldId)
  const [, , stateHelpers] = useField(props.stateFieldId)
  const childRef = useRef({})
  const [loading, setLoading] = useState(false)
  const [userErrors, , handleErrors, , clearErrors] = useErrorHandler()
  const [suggestions, setSuggestions] = useState<ILocation[]>([])
  const [noSuggestions, setNoSuggestions] = useState<string>(null)

  const getApiSuggestions = async (query) => {
    if (query?.length <= 2) {
      setSuggestions([])
      return []
    }
    setLoading(true)
    await AddressService.getLocations(query)
      .then((response) => {
        return response.result
          .filter(
            (item: ILocation) =>
              item.postcode.includes(query) ||
              item.location.toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 30)
      })
      .then((results) => {
        clearErrors()
        setNoSuggestions(null)
        setSuggestions([])
        if (results.length > 0) {
          setSuggestions(results)
        } else {
          setNoSuggestions('No suburb or postcode found')
        }
      })
      .catch((error) => {
        setSuggestions([])
        handleErrors(error)
      })
      .finally(() => setLoading(false))
  }

  const setSelection = (selection: ILocation) => {
    postCodeHelpers.setValue(selection.postcode)
    stateHelpers.setValue(selection.state)
  }

  const renderSuggestion = (suggestion: ILocation, index: number) => {
    return (
      <div
        role="button"
        onClick={() => {
          childRef.current?.setSelection(suggestion.location)
          setSelection(suggestion)
        }}
        onKeyPress={(event) => {
          if (event.charCode == 13) {
            childRef.current?.setSelection(suggestion.location)
            setSelection(suggestion)
          }
        }}
        key={index}
        tabIndex={0}
      >
        {suggestion.location}, {suggestion.postcode} {suggestion.state}
      </div>
    )
  }

  const clearNoSuggestionOrError = () => {
    clearErrors()
    setNoSuggestions(null)
  }

  return (
    <>
      <FWOAutoComplete
        ref={childRef}
        name={props.suburbFieldId}
        label={props.suburbLabel}
        tooltip={props.tooltip}
        requests={getApiSuggestions}
        renderSuggestion={renderSuggestion}
        noSuggestions={noSuggestions}
        suggestions={suggestions}
        showreadonly={props.showreadonly}
        visible={true}
        loading={loading}
        userErrors={userErrors}
        clearNoSuggestionOrError={clearNoSuggestionOrError}
        placeholder={props.placeholder}
        title={props.titleSuburb}
      />
      <Conditional if={props.displayOrder}>
        <p></p>
        <FWOTextInput
          name={props.stateFieldId}
          required={props.requiredState}
          label={props.stateLabel}
          ariaLabel={props.stateLabel}
          placeholder={props.stateLabel}
          type="text"
          showreadonly={props.showotherreadonly ? false : true}
          title={props.titleState}
        />

        <p></p>
        <FWOTextInput
          name={props.postcodeFieldId}
          required={props.requiredPostcode}
          label={props.postcodeLabel}
          ariaLabel={props.postcodeLabel}
          placeholder={props.postcodeLabel}
          type="text"
          showreadonly={props.showotherreadonly ? false : true}
          title={props.titlePostcode}
        />
      </Conditional>
      <Conditional if={props.displayOrder === null || !props.displayOrder}>
        <p></p>
        <FWOTextInput
          name={props.postcodeFieldId}
          required={props.required}
          label={props.postcodeLabel}
          ariaLabel={props.postcodeLabel}
          placeholder={props.postcodeLabel}
          type="text"
          showreadonly={props.showotherreadonly ? false : true}
          title={props.titlePostcode}
        />
        <p></p>
        <FWOTextInput
          name={props.stateFieldId}
          required={props.required}
          label={props.stateLabel}
          ariaLabel={props.stateLabel}
          placeholder={props.stateLabel}
          type="text"
          showreadonly={props.showotherreadonly ? false : true}
          title={props.titleState}
        />
      </Conditional>
    </>
  )
}

export default FWOSuburbPostcodeLookup
